import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useRef, useEffect, useState, useCallback } from 'react';
import * as React from 'react';
import { PaymentMethod } from '../constants/paymentMethod';
import { usePaymentDetails } from '../providers/PaymentDetailsProvider';
import { usePayment } from '../providers/PaymentProvider';
import ClaimContainer from './ClaimContainer';
import ErrorTagContainer from './ErrorTagContainer';
import InfoContainer from './InfoContainer';
import TokenDetailsContainer from './TokenDetailsContainer';

export interface PayWithGasContentProps {
  title?: string;
  description?: string;
  buttonName?: string;
  breadcrumbsLabel?: string;
  agreeTermsAndConditionsMessage?: string;
  privacyPolicyLabel?: string;
  termsAndConditionsLabel?: string;
  image?: string;
  termsSeparatorText?: string;
  buttonNameSecondary?: string;
}

export interface PayWithGasContainerProps {
  termsUrl: string;
  isLoading: boolean;
  privacyUrl?: string;
  content?: PayWithGasContentProps;
  price?: string;
  validation?: 'clear' | 'no_balance' | 'wrong_network';
  networkName?: string;
  currency?: string;
  loaderDescription?: string;
  onClickClaimNow: () => void;
  onClickSwitchNetwork: () => void;
}

const PayWithGasContainer = ({
  termsUrl,
  privacyUrl,
  content,
  price = 'Free',
  isLoading,
  validation,
  networkName,
  loaderDescription,
  onClickClaimNow,
  onClickSwitchNetwork
}: PayWithGasContainerProps) => {
  const theme = useTheme();

  const { error, isApproveLoading } = usePayment();
  const { paymentDetails } = usePaymentDetails();
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const [terms, setTerms] = useState<boolean>(false);
  const [termsValidation, setTermsValidation] = useState<string>('');
  const [policy, setPolicy] = useState<boolean>(false);

  useEffect(() => {
    if (dialogContentRef.current) {
      dialogContentRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, []);

  const onChangeTerms = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setTermsValidation('');
    setTerms(event.target.checked);
  }, []);

  const onChangePolicy = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setTermsValidation('');
    setPolicy(event.target.checked);
  }, []);

  const onClickContinue = useCallback(() => {
    if (!terms || !policy) {
      setTermsValidation('Please agree to the T&Cs to continue');

      return;
    }
    onClickClaimNow();
  }, [terms, policy, onClickClaimNow]);

  return (
    <div ref={dialogContentRef}>
      <ClaimContainer
        title={isApproveLoading ? 'Approval Required' : content?.title || 'Confirm Transaction'}
        description={
          isApproveLoading
            ? 'To mint your digital collectible for free by using your mint pass, you will need to give permissions to burn it. To do this, please sign the transaction.'
            : content?.description || 'You will be charged a gas fee. Make sure you have sufficient balance.'
        }
      />
      {!isApproveLoading && (
        <>
          <TokenDetailsContainer price={price} />
          {/* <PaymentMethodView disabled={isLoading} validation={validation ?? ''} /> */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '20px'
            }}
          >
            <Checkbox
              disabled={isLoading}
              sx={{
                marginTop: '-7px',
                color: error ? theme.MojitoClaim?.error : theme.palette.primary.main,
                '&.Mui-checked': {
                  color: theme.palette.primary.main
                }
              }}
              checked={terms}
              onChange={onChangeTerms}
              inputProps={{
                'aria-label': 'controlled'
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontFamily: theme?.MojitoClaim?.font?.tertiary,
                  color: theme?.ClaimTemplate?.collectionHeaderColor,
                  fontSize: '0.8rem',
                  fontWeight: 400,
                  marginBottom: '5px'
                }}
              >
                {content?.agreeTermsAndConditionsMessage ||
                  'By clicking the button below, I confirm that I have read and agree to the '}
                <a
                  style={{
                    textDecorationLine: 'underline',
                    color: theme?.MojitoClaim?.linkColor,
                    fontSize: '0.8rem'
                  }}
                  href={termsUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {content?.termsAndConditionsLabel || 'Program Site & NFT Terms'}
                </a>
                {privacyUrl ? content?.termsSeparatorText || ' and ' : ''}
                {privacyUrl && (
                  <a
                    style={{
                      textDecorationLine: 'underline',
                      color: theme?.MojitoClaim?.linkColor,
                      fontSize: '0.8rem'
                    }}
                    href={privacyUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {content?.privacyPolicyLabel || 'Privacy Policy.'}
                  </a>
                )}
              </Typography>
              {!terms && termsValidation && (
                <Typography
                  sx={{
                    color: theme.MojitoClaim?.error,
                    fontFamily: theme?.MojitoClaim?.font?.tertiary,
                    fontSize: '0.8rem'
                  }}
                >
                  {termsValidation}
                </Typography>
              )}
            </Box>
          </Box>
        </>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          marginBottom: '20px'
        }}
      >
        <Checkbox
          disabled={isLoading}
          sx={{
            marginTop: '-7px',
            color: error ? theme.MojitoClaim?.error : theme.palette.primary.main,
            '&.Mui-checked': {
              color: theme.palette.primary.main
            }
          }}
          checked={policy}
          onChange={onChangePolicy}
          inputProps={{
            'aria-label': 'controlled'
          }}
        />
        <Box>
          <Typography
            sx={{
              fontFamily: theme?.MojitoClaim?.font?.tertiary,
              color: theme?.ClaimTemplate?.collectionHeaderColor,
              fontSize: '0.8rem',
              fontWeight: 400
            }}
          >
            I acknowledge and agree that by proceeding with the purchase, I waive any right to request a refund.
          </Typography>
          {(error || (!policy && termsValidation)) && (
            <Typography
              sx={{
                color: theme.MojitoClaim?.error,
                fontFamily: theme?.MojitoClaim?.font?.tertiary,
                fontSize: '0.8rem'
              }}
            >
              Please agree to continue
            </Typography>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          marginBottom: '20px'
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: theme?.MojitoClaim?.font?.tertiary,
              color: theme?.ClaimTemplate?.collectionHeaderColor,
              fontSize: '0.8rem',
              fontWeight: 400
            }}
          >
            <strong>Note:</strong> Fluctuating Ethereum price may affect NFT value
          </Typography>
        </Box>
      </Box>
      {isLoading && paymentDetails.paymentType === PaymentMethod.crypto && (
        <InfoContainer
          style={{
            marginTop: isApproveLoading ? '24px' : 0
          }}
          description={
            loaderDescription || 'Follow wallet instruction to approve. Please do not close or reload the page...'
          }
        />
      )}
      {validation !== 'clear' && (
        <ErrorTagContainer>
          {validation === 'no_balance' ? (
            <div>
              <span>
                You don’t have enough ETH to complete this action. Please ensure your wallet has enough to cover the
                item price and gas fees.
              </span>
            </div>
          ) : validation === 'wrong_network' ? (
            <div>
              Please switch to{' '}
              <Box
                onClick={onClickSwitchNetwork}
                sx={{
                  textDecorationLine: 'underline',
                  display: 'inline-flex',
                  cursor: 'pointer'
                }}
              >
                {networkName}
              </Box>{' '}
              in order to complete your claim
            </div>
          ) : (
            <div />
          )}
        </ErrorTagContainer>
      )}
      <Button
        disabled={isLoading}
        startIcon={
          isLoading && (
            <CircularProgress
              sx={{
                color: theme?.palette?.text?.primary
              }}
              size="14px"
            />
          )
        }
        sx={{
          width: '100%',
          textTransform: 'none'
        }}
        onClick={onClickContinue}
      >
        {isLoading
          ? 'Processing'
          : validation === 'no_balance'
            ? 'Refresh balance'
            : validation === 'wrong_network'
              ? 'Switch network'
              : content?.buttonName || 'Continue'}
      </Button>
    </div>
  );
};

export default PayWithGasContainer;
